import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import { formatDate } from "../../utils/dateUtils";
import { encrypt } from "../../utils/encryption";
import alertService from "../../utils/alertService";
import salesPaymentService from "../../services/salesPaymentService";

function SalesPaymentMasterPage() {
  const navigate = useNavigate();
  const basePath = "/sales-payment-master";

  // Main CRUD Hook for Sales Master Payment Summaries
  const endpoints = useMemo(
    () => ({
      list: "/sales-payment-master/summary",
      create: "/sales-payment-master",
      edit: (id) => `/sales-payment-master/${id}`,
      update: (id) => `/sales-payment-master/${id}`,
      delete: (id) => `/sales-payment-master/${id}`,
    }),
    []
  );

  const {
    rows,
    isLoading,
    error,
    filters,
    pagination,
    sortConfig,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "sellingMasterId",
    updateMethod: "patch",
    initialFilters: { search: "" },
    initialSort: { key: "createdDate", order: "DESC" },
  });

  // Navigate to Manage Payments Page
  const handleManagePayments = (item) => {
    navigate(`${basePath}/${encrypt(item.sellingMasterId)}/payments`, {
      state: { sellingMaster: item },
    });
  };

  // Toggle isReceived status directly from table
  const handleToggleReceived = async (item) => {
    const isCurrentReceived = Boolean(item?.isReceived);
    const nextStatus = !isCurrentReceived;

    const actionTitle = nextStatus ? "Complete Payment" : "Unlock Payment";
    const message = nextStatus
      ? `Are you sure you want to mark Invoice #${item?.invoiceNo || ""} as Fully Received / Completed?<br/><br/><span class="text-xs text-slate-500">This will lock the payment record.</span>`
      : `Are you sure you want to unlock Invoice #${item?.invoiceNo || ""}?<br/><br/><span class="text-xs text-slate-500">This will allow adding and modifying payment transactions again.</span>`;

    const confirm = await alertService.confirm(message, {
      title: actionTitle,
      confirmButtonText: nextStatus ? "Yes, Complete Payment" : "Yes, Unlock Payment",
    });

    if (!confirm.isConfirmed) return;

    try {
      await salesPaymentService.toggleReceivedStatus(item.sellingMasterId, nextStatus);
      alertService.success(
        nextStatus
          ? "Payment marked as completed and locked successfully."
          : "Payment unlocked successfully."
      );
      if (typeof refresh === "function") {
        refresh();
      }
    } catch (err) {
      alertService.error(err.message || "Failed to update payment status.");
    }
  };

  // Table Columns Definition
  const tableColumns = useMemo(
    () => [
      {
        key: "invoiceNo",
        label: "Invoice No",
        sortable: true,
        className: "font-bold text-slate-800 dark:text-white",
        render: (item) => String(item?.invoiceNo || "N/A"),
      },
      {
        key: "date",
        label: "Invoice Date",
        sortable: true,
        render: (item) => formatDate(item?.date),
      },
      {
        key: "customerName",
        label: "Customer",
        sortable: true,
        render: (item) => item?.customerName || "N/A",
      },
      {
        key: "companyName",
        label: "Company",
        sortable: true,
        render: (item) => item?.companyName || "N/A",
      },
      {
        key: "invoiceAmount",
        label: "Invoice Amount",
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => (
          <span className="font-bold text-slate-700 dark:text-slate-200">
            ₹{Number(item?.invoiceAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        key: "tdsAmount",
        label: (
          <div className="flex flex-col items-end leading-tight">
            <span>TDS Amount</span>
            <span className="text-[9px] text-slate-400 dark:text-slate-500 font-normal normal-case tracking-normal block mt-0.5">
              (2% of Taxable Amount)
            </span>
          </div>
        ),
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => (
          <div className="flex flex-col items-end">
            <span className="font-bold text-amber-600 dark:text-amber-400">
              ₹{Number(item?.tdsAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </span>
            <span className="text-[10px] text-slate-400 dark:text-slate-500 font-normal">
              2% of ₹{Number(item?.taxableAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </span>
          </div>
        ),
      },
      {
        key: "totalAmount",
        label: "Total Amount",
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => (
          <span className="font-bold text-slate-700 dark:text-slate-200">
            ₹{Number(item?.totalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        key: "receivedAmount",
        label: "Received Amount",
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => (
          <span className="font-bold text-emerald-600 dark:text-emerald-400">
            ₹{Number(item?.receivedAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        key: "remainingReceivableAmount",
        label: "Remaining Amount",
        headerClassName: "text-right",
        className: "text-right font-mono",
        render: (item) => {
          const remaining = Number(item?.remainingReceivableAmount || 0);
          if (remaining < 0) {
            return (
              <span className="font-bold text-indigo-600 dark:text-indigo-400" title="Excess Received">
                +₹{Math.abs(remaining).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </span>
            );
          }
          return (
            <span
              className={`font-bold ${
                remaining === 0
                  ? "text-slate-400"
                  : "text-amber-600"
              }`}
            >
              ₹{remaining.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              })}
            </span>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        headerClassName: "text-center",
        className: "text-center",
        render: (item) => {
          const isReceived = Boolean(item?.isReceived);
          const received = Number(item?.receivedAmount || 0);

          let badgeClass = "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400";
          let statusText = "Pending";

          if (isReceived) {
            badgeClass = "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400";
            statusText = "Received";
          } else if (received > 0) {
            badgeClass = "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400";
            statusText = "Partially Received";
          }

          return (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${badgeClass}`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  isReceived
                    ? "bg-emerald-500"
                    : received > 0
                    ? "bg-amber-500"
                    : "bg-rose-500"
                }`}
              />
              {statusText}
            </span>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        renderActions={(item) => {
          const isReceived = Boolean(item?.isReceived);
          return (
            <div className="flex items-center gap-1.5">
              <Button
                variant="secondary"
                size="sm"
                className={`flex items-center gap-1 h-8 px-2.5 text-xs font-bold uppercase tracking-wider transition-all active:scale-95 ${
                  isReceived
                    ? "border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 dark:border-slate-700 dark:text-slate-400"
                    : "border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300"
                }`}
                onClick={() => handleManagePayments(item)}
                title={isReceived ? "View Payments" : "Manage Payments"}
              >
                <i className="material-icons text-sm">{isReceived ? "visibility" : "payments"}</i>
                {isReceived ? "View" : "Receive"}
              </Button>

              <Button
                variant="secondary"
                size="sm"
                className={`flex items-center gap-1 h-8 px-2 text-xs font-bold transition-all active:scale-95 ${
                  isReceived
                    ? "border-amber-200 text-amber-700 bg-amber-50/50 hover:bg-amber-100 dark:border-amber-900/50 dark:text-amber-400"
                    : "border-emerald-200 text-emerald-700 bg-emerald-50/50 hover:bg-emerald-100 dark:border-emerald-900/50 dark:text-emerald-400"
                }`}
                onClick={() => handleToggleReceived(item)}
                title={isReceived ? "Unlock payment to edit" : "Complete and lock payment"}
              >
                <i className="material-icons text-sm">{isReceived ? "lock_open" : "task_alt"}</i>
                {isReceived ? "Unlock" : "Complete"}
              </Button>
            </div>
          );
        }}
        showActions={true}
        searchKey="search"
        searchPlaceholder="Search by Invoice No, Customer or Company..."
        tableScrollHeight="58vh"
      />
    </div>
  );
}

export default SalesPaymentMasterPage;
